// React Imports
import { ReactElement, useEffect, useState } from "react";

// Style Imports
import "./portfolio-agreement.styles.scss";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import PortfolioAgreement from "src/models/portfolio-agreement.model";
import { ColumnsType } from "antd/es/table";
import { DatePicker, Radio, Tooltip } from "antd";
import moment from "moment";
import CustomTable from "src/components/custom-table/custom-table";
import PortfolioAgreementService from "./portfolio-agreement.service";
import AgreementReviewService from "../agreement-review/agreement-review.service";
import RejectModal from "../agreement-review/components/reject-modal";
import clsx from "clsx";
import { ReactSVG } from "react-svg";
import CustomModal from "src/components/custom-modal/custom-modal";
import CustomSearch from "src/components/custom-search/custom-search";
import PdfViewer from "src/components/pdf-viewer";
import { generateExcel } from "src/utils/helper/functions";
import { AuditOutlined, SendOutlined } from "@ant-design/icons";
import toast from "react-hot-toast";
import Label from "src/components/label/label.component";
import { AgreementReviewStatus } from "./agreement-status";
import { REVIEW_PERMISSION } from "../portfolio-agreements-bulk/types";
import { applicableDate } from "./agreement-dates";
import {
  AgreementStatus,
  isStatus,
  REVIEW_STATUS_FILTERS,
  reviewStatusView,
  STATUS_FILTERS,
  statusView,
} from "./agreement-status";

let searchTimer = null;

interface PortfolioAgreementComponentProps {
  exportClicked: boolean;
}

const PortfolioAgreementComponent: React.FC<
  PortfolioAgreementComponentProps
> = ({ exportClicked }: PortfolioAgreementComponentProps): ReactElement => {
  const user = useSelector((state: any) => state.user);
  const navigate = useNavigate();
  const loadingState = useSelector((state: any) => state?.loading);
  const portfolioAgreementService = new PortfolioAgreementService();
  // Returning from the list is the same operation as "Return to client" on the
  // review page, so it goes through the same service — 409 handling included.
  const agreementReviewService = new AgreementReviewService();
  const [portfolioAgreementList, setPortfolioAgreementList] = useState<
    PortfolioAgreement[]
  >([]);
  const [exportData, setExportData] = useState<any>([]);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [returning, setReturning] = useState(false);
  const [openRegenerateModal, setOpenRegenerateModal] = useState(false);
  const [record, setRecord] = useState<PortfolioAgreement>();
  const [regenerateRecord, setRegenerateRecord] = useState<PortfolioAgreement>();
  const [responseDate, setResponseDate] = useState<any>({});
  const [searchWord, setSearchWord] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [filters, setFilters] = useState(null);
  const [typeFilters, setTypeFilters] = useState(null);
  const [openViewPdf, setOpenViewPdf] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [openGenerateKycModal, setOpenGenerateKycModal] = useState(false);
  const [viewOnly, setViewOnly] = useState(true);
  const [asOfDate, setAsOfDate] = useState(null);

  useEffect(() => {
    fetchPortfolioAgreementListData();
  }, [searchWord, sortField, sortOrder, filters, typeFilters]);

  useEffect(() => {
    if (exportClicked) {
      exportPortfolioAgreementListData();
    }
  }, [exportClicked]);

  const handleRegeneratePdf = async (portfolioId: number): Promise<void> => {
    try {
      const response: any = await portfolioAgreementService.regenerateAgreement(portfolioId, viewOnly, asOfDate);

      if (response) {

        // Check if response itself is the base64 string or if it's nested in response.data
        const pdfData = typeof response === 'string' ? response : (response?.data || response?.pdf || response?.file);
        downloadPdf(pdfData, "portfolio.pdf");
        setOpenGenerateKycModal(false);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to regenerate PDF");
    } finally {
      setOpenGenerateKycModal(false);
    }
  };

  const downloadPdf = (base64String, fileName) => {
    // Clean the base64 string - remove data URL prefix if present and whitespace
    let cleanBase64 = base64String;
    if (typeof cleanBase64 === 'string') {
      // Remove data URL prefix if present (e.g., "data:application/pdf;base64,")
      if (cleanBase64.includes(',')) {
        cleanBase64 = cleanBase64.split(',')[1];
      }
      // Remove any whitespace/newlines
      cleanBase64 = cleanBase64.replace(/\s/g, '');
    }

    // Convert Base64 to Blob
    const byteCharacters = atob(cleanBase64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: "application/pdf" });

    // Create a link element
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName || "document.pdf"; // Default file name

    // Append the link to the body (required for Firefox)
    document.body.appendChild(link);

    // Trigger the download
    link.click();

    // Remove the link from the document
    document.body.removeChild(link);
  };

  const exportPortfolioAgreementListData = async (): Promise<void> => {
    const data: any =
      await portfolioAgreementService.exportPortfolioAgreementListData(
        1,
        searchWord,
        sortField,
        sortOrder,
        filters,
        1000000,
        typeFilters
      );

    if (data !== undefined) {
      setPortfolioAgreementList(data?.data?.data?.portfolios);
      generateExcel(
        [
          {
            sheetName: `Portfolios Agreements`,
            sheetContent: data?.data?.data?.portfolios,
          },
        ],
        `Portfolios Agreements`
      );
    }
  };

  const fetchPortfolioAgreementListData = async (page = 1): Promise<void> => {
    const data: any =
      await portfolioAgreementService.getPortfolioAgreementListData(
        page,
        searchWord,
        sortField,
        sortOrder,
        filters,
        typeFilters
      );
    if (data !== undefined) {
      setResponseDate(data?.data?.data);
      setPortfolioAgreementList(data?.data?.data?.portfolios);
    }
  };

  const AGREEMENT_TYPES = {
    APPENDIX: "Appendix",
    PORTFOLIO_AGREEMENT: "Portfolio agreement",
  };

  const getTypeLabel = (type: string): string =>
    AGREEMENT_TYPES[type] || type || "-";

  const getDocumentUrl = (record: PortfolioAgreement): string =>
    record?.type === "APPENDIX"
      ? record?.appendix || record?.file || record?.agreementPdf
      : record?.agreementPdf;

  const columns: ColumnsType<PortfolioAgreement> = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      fixed: "left",
      render: (_, __, index: number) => <span>{index + 1}</span>,
    },
    {
      title: "Portfolio code",
      dataIndex: "portfolioCode",
      key: "portfolioCode",
      sorter: true,
    },
    {
      title: "Client name",
      dataIndex: "clientNameEn",
      key: "clientNameEn",
      render: (text) => <span className="text-gray-500">{text || "-"}</span>,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      filters: [
        {
          text: "Appendix",
          value: "APPENDIX",
        },
        {
          text: "Portfolio agreement",
          value: "PORTFOLIO_AGREEMENT",
        },
      ],
      render: (text) => (
        <span
          className={clsx(
            "p-2 rounded-md text-xs whitespace-nowrap",
            text === "APPENDIX"
              ? "text-blue-500 bg-blue-100"
              : "text-black-300 bg-gray-400"
          )}
        >
          {getTypeLabel(text)}
        </span>
      ),
    },
    {
      title: "Wealth manager",
      // Sent as sortField=ae1. The name still arrives as `wealthManagerName`,
      // so it is read from the record rather than through dataIndex.
      dataIndex: "ae1",
      key: "wealthManagerName",
      sorter: true,
      render: (_, record: any) => (
        <span className="text-gray-500">{record?.wealthManagerName || "-"}</span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sorter: true,
      filters: STATUS_FILTERS,
      render: (text) => {
        const view = statusView(text);
        return (
          <span className={clsx("agreement-chip", `tone-${view.tone}`)}>
            {view.label}
          </span>
        );
      },
    },
    {
      title: "Review status",
      dataIndex: "reviewStatus",
      key: "reviewStatus",
      filters: REVIEW_STATUS_FILTERS,
      render: (text) => {
        const view = reviewStatusView(text);
        return (
          <span className={clsx("agreement-chip", `tone-${view.tone}`)}>
            {view.label}
          </span>
        );
      },
    },
    {
      title: "Reject reason",
      dataIndex: "rejectionReasonId",
      key: "rejectionReasonId",
      sorter: true,
      // The payload carries `rejectionReason: { description }`; the old code
      // read a flat `rejectReason` that does not exist, so this column always
      // showed "-". The flat field is still accepted as a fallback.
      render: (_, record: any) => (
        <span className="date text-gray-500">
          {record?.rejectionReason?.description || record?.rejectReason || "-"}
        </span>
      ),
    },
    {
      title: "Agreement date",
      dataIndex: "agreementDate",
      key: "agreementDate",
      sorter: true,
      // An agreement has no date of its own — the portfolio open date is its
      // agreement date. Only an appendix uses `agreementDate`.
      render: (_, record: any) => {
        const value = applicableDate(record);
        return (
          <span className="date text-gray-500">
            {value ? moment(value).format("MMM DD YYYY") : "-"}
          </span>
        );
      },
    },
    {
      title: "Updated at",
      dataIndex: "updatedAt",
      key: "updatedAt",
      sorter: true,
      render: (text) => (
        <span className="date text-gray-500">
          {moment(text).format("MMM DD YYYY hh:mm A")}
        </span>
      ),
    },
    {
      title: "Created at",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: true,
      render: (text) => (
        <span className="date text-gray-500">
          {moment(text).format("MMM DD YYYY hh:mm A")}
        </span>
      ),
    },
    {
      title: "Actions",
      dataIndex: "action",
      // Room for up to three icons at their larger size.
      width: 150,
      key: "action",
      render: (_, record: PortfolioAgreement) => (
        <div className="agreement-actions flex items-center gap-3">
          {/* Only records awaiting a WM decision can be reviewed; the API
              rejects anything else with a 409. */}
          {(record?.reviewStatus ?? "").toUpperCase() ===
            AgreementReviewStatus.PENDING_REVIEW &&
            user?.permissions?.includes(REVIEW_PERMISSION) && (
              <Tooltip title="Review">
                <span
                  onClick={() =>
                    navigate(`/portfolio/agreement-review/${record?.id}`)
                  }
                  className="cursor-pointer"
                >
                  <AuditOutlined />
                </span>
              </Tooltip>
            )}

          {/* Was `status !== "Done" && status !== "Pending"`, which with the old
              values could only ever be true for Rejected. Stated directly so a
              new state (PENDING_REVIEW) does not silently start showing it. */}
          {isStatus(record?.status, AgreementStatus.REJECTED) &&
            user?.permissions?.includes("resend_new_Portfolio_aggreement") && (
              <Tooltip title="Return to client">
                <span
                  onClick={() => {
                    setRecord(record);
                    setOpenConfirmationModal(true);
                  }}
                  className="cursor-pointer"
                >
                  <SendOutlined />
                </span>
              </Tooltip>
            )}

          {isStatus(record?.status, AgreementStatus.DONE) && user?.permissions?.includes("portfolio_agreement_regenerate") && <Tooltip title="Regenerate Agreement">
            <span
              onClick={() => {
                setRegenerateRecord(record);
                setOpenGenerateKycModal(true);
              }}
              className="cursor-pointer"
            >
              <ReactSVG src={"/images/resend.svg"} />
            </span>
          </Tooltip>}

          {getDocumentUrl(record) && (
            <Tooltip title="View PDF">
              <span
                onClick={() => {
                  setSelectedPdf(getDocumentUrl(record));
                  setOpenViewPdf(true);
                }}
                className="cursor-pointer"
              >
                <ReactSVG src={"/images/view-pdf.svg"} />
              </span>
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  const onSearch = (e) => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      setSearchWord(e.target.value);
    }, 1000);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setSortField(sorter.field);
    setSortOrder(sorter.order);
    setFilters(filters.status);
    setTypeFilters(filters.type);
  };

  return (
    <div className="closure-request-container flex flex-col">
      <div className="flex items-center justify-between rounded-lg bg-white py-[10px] px-5">
        <p className=" text-black-300 text-base font-bold">
          {!loadingState ? responseDate?.totalCount : ""} requests
        </p>
        <div className="flex items-center">
          <CustomSearch onChange={(event) => onSearch(event)} />
        </div>
      </div>

      <CustomTable
        columns={columns}
        data={portfolioAgreementList}
        showPagination={true}
        pageSize={responseDate?.pageSize}
        totalCount={responseDate?.totalCount}
        page={responseDate?.currentPage}
        controlledPage
        onPageChange={(page) => {
          fetchPortfolioAgreementListData(page);
        }}
        handleTableChange={handleTableChange}
      />

      {/* Same confirmation and same endpoint as "Return to client" on the review
          page: POST :id/review with { approved: false, feedback }. */}
      <RejectModal
        isVisible={openConfirmationModal}
        busy={returning}
        onClose={() => setOpenConfirmationModal(false)}
        onConfirm={async (feedback) => {
          setReturning(true);
          const outcome = await agreementReviewService.decide(
            String(record?.id),
            { approved: false, feedback }
          );
          setReturning(false);
          // A 409 means the record already moved on; refresh either way so the
          // list stops offering an action that is no longer available.
          if (outcome !== "error") {
            setOpenConfirmationModal(false);
            fetchPortfolioAgreementListData();
          }
        }}
      />
      {openViewPdf && (
        <CustomModal
          isVisible={openViewPdf}
          centered
          width={1100}
          showClose={false}
          onClose={() => {
            setOpenViewPdf(false);
          }}
          className="edit-modal"
        >
          <div className="flex flex-col items-center justify-center">
            <PdfViewer fileUrl={selectedPdf} />
          </div>
        </CustomModal>
      )}

      <CustomModal
        isVisible={openGenerateKycModal}
        centered
        showClose={false}
        onClose={() => {
          setOpenGenerateKycModal(false);
        }}
        className="generate-modal"
      >
        <div className="w-full h-full py-5 bg-white rounded-xl shadow-[0px_0px_4px_0px_rgba(0,0,0,0.16)] flex-col justify-center items-end gap-5 inline-flex">
          <div className="flex-col justify-start items-end gap-10 flex">
            <div className="flex-col justify-start items-start flex">
              <div className="self-stretch h-7 flex-col justify-start items-start gap-5 flex">
                <div className="self-stretch h-7 px-5 flex-col justify-start items-start gap-4 flex">
                  <div className="self-stretch justify-start items-start gap-4 inline-flex">
                    <div className="grow shrink basis-0 text-black text-2xl font-bold  tracking-tight">
                      Generate KYC PDF
                    </div>
                    <div className="w-6 h-6 justify-center items-center flex">
                      <div className="w-6 h-6 relative"></div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="self-stretch flex-col justify-start items-start flex">
                <div className="px-5 justify-start items-start gap-5 inline-flex">
                  <div className="self-stretch py-5 flex-col justify-start items-start gap-5 inline-flex">
                    <div className="self-stretch text-[#535353] text-sm font-medium  leading-7">
                      You're trying to generate KYC PDF, please select on of the
                      below options
                    </div>
                    <Radio.Group
                      defaultValue={0}
                      className="flex flex-col gap-3"
                      onChange={(e) => {
                        if (e.target.value === 1) {
                          setViewOnly(false);
                        } else {
                          setViewOnly(true);
                        }
                        if (e.target.value === 0) {
                          setViewOnly(true);
                        }
                      }}
                    >
                      <Radio value={0}>View only</Radio>
                      <Radio value={1}>Upload to PROSPERO only</Radio>

                    </Radio.Group>

                    <div className="flex flex-col gap-1 w-full">
                      <Label text="As of Date (Optional)" />
                      <DatePicker
                        placeholder="Select date"
                        showToday={false}
                        onChange={(date) => setAsOfDate(date?.toISOString())}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="self-stretch px-5 justify-end items-start gap-5 inline-flex">
            <div
              onClick={() => setOpenGenerateKycModal(false)}
              className="cursor-pointer grow shrink basis-0 h-12 p-4 bg-white rounded-lg border border-[#002f6c] justify-center items-center gap-2.5 flex"
            >
              <div className="text-center text-[#002f6c] text-sm font-bold  leading-none">
                Cancel
              </div>
            </div>
            <div
              onClick={async () => handleRegeneratePdf(regenerateRecord?.id)}
              className="cursor-pointer grow shrink basis-0 h-12 p-3 bg-[#002f6c] rounded-lg justify-center items-center gap-2 flex"
            >
              <div className="text-center text-white text-sm font-bold  leading-none">
                Submit
              </div>
            </div>
          </div>
        </div>
      </CustomModal>
    </div>
  );
};

export default PortfolioAgreementComponent;
